import { AsyncPipe } from '@angular/common'
import { Component, inject, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslatePipe } from '@ngx-translate/core'
import { combineLatest, filter, map, shareReplay } from 'rxjs'
import { AnalyticsDashboardComponent } from '../analytics-dashboard/analytics-dashboard.component'
import { ClientTeam, ClientsService } from '../clients.service'
import { MetricsService } from '../metrics.service'
import { scopedId } from '../multi-environment/environment-merge'
import { SELF_ENVIRONMENT } from '../multi-environment/support-environment'
import { MultiFilterComponent } from '../multi-filter/multi-filter.component'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { UsersService } from '../users.service'

@Component({
  selector: 'app-client-dashboard',
  standalone: true,
  imports: [ToolbarComponent, TranslatePipe, AnalyticsDashboardComponent, MultiFilterComponent, SpinnerComponent, AsyncPipe],
  templateUrl: './client-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './client-dashboard.component.scss',
})
export class ClientDashboardComponent {
  route = inject(ActivatedRoute)
  router = inject(Router)
  clientsService = inject(ClientsService)
  usersService = inject(UsersService)
  metricsService = inject(MetricsService)

  currentUser$ = this.usersService.currentUser$
  client$ = this.clientsService.getCurrentUserClient().pipe(shareReplay(1))

  /**
   * Null while it is still loading as well as for a client with no contract recorded, which is the
   * same thing as far as the dashboard is concerned: neither offers the subscription period.
   */
  subscriptionUsage$ = this.metricsService.getSubscriptionUsage().pipe(
    map((result) => result.data ?? null),
    shareReplay(1)
  )

  teams$ = this.client$.pipe(
    filter((result) => result.isSuccess),
    map((result) => result.data?.teams ?? []),
    shareReplay(1)
  )

  private selectedTeamIdsFromUrl$ = this.route.queryParamMap.pipe(
    map((params) =>
      params
        .getAll('teamIds')
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id))
    )
  )

  /**
   * An empty selection means the whole client, which is what this dashboard shows by default.
   *
   * Scoped to the environment serving this page, which is the only one a customer's dashboard ever
   * reads: the analytics screens are shared with support's cross-environment view, where the plain
   * row id would not say which environment's team it means.
   */
  teamIds$ = combineLatest([this.teams$, this.selectedTeamIdsFromUrl$]).pipe(
    map(([teams, selectedIds]) => {
      const teamIdsOfClient = teams.map((team) => team.id)
      const validSelectedIds = selectedIds.filter((id) => teamIdsOfClient.includes(id))
      const selection = validSelectedIds.length > 0 ? validSelectedIds : teamIdsOfClient
      return selection.map((teamId) => scopedId(SELF_ENVIRONMENT, teamId))
    }),
    shareReplay(1)
  )

  selectedTeamIds$ = combineLatest([this.teams$, this.selectedTeamIdsFromUrl$]).pipe(
    map(([teams, selectedIds]) => selectedIds.filter((id) => teams.some((team) => team.id === id)))
  )

  displayTeam(team: ClientTeam): string {
    return team.name
  }

  displaySelectedTeams(teams: ClientTeam[]) {
    return (ids: number[]) =>
      teams
        .filter((team) => ids.includes(team.id))
        .map((team) => team.name)
        .join(', ')
  }

  async onTeamChange(selectedIds: number[]) {
    await this.router.navigate([], {
      queryParams: selectedIds.length ? { teamIds: selectedIds } : { teamIds: null },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  async onTeamClear() {
    await this.router.navigate([], {
      queryParams: { teamIds: null },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }
}
